import { useFormContext, NumberField, SelectField } from '@calendly/form'
import { FieldError, useErrorId } from '@calendly/form/parts'
import { I18n } from '@calendly/i18n'
import { DateRangePickerInput } from '@calendly/ui/components/date-range-picker-input'
import { BodyText, LabelText, TitleText } from '@calendly/ui/components/typography'
import { ViewSwitcher } from '@calendly/ui/components/view-switcher'
import classNames from 'classnames'
import moment from 'moment-timezone'
import { useCallback, forwardRef } from 'react'
import { useSelector } from 'react-redux'

import { Increments, useValidateOnChange } from 'components/event_type_edit'
import { getPrettyDateRange } from 'components/event_type_edit/booking_range/get_pretty_date_range'
import { useErrorAfterInitialTouch } from 'features/event_type_editor'
import { dateNotationSelector, timezoneSelector } from 'selectors/current_user'

import {
  compactFieldGroupCss,
  compactFieldStackCss,
  dateRangeDetailCss,
  dateRangeSwitchCss,
  fieldGroupCss,
  fieldHelperCss,
  fixedDateRangeInputCss,
  minimumNoticeRowCss,
  minimumNoticeNumberFieldCss,
  minimumNoticeUnitSelectCss,
  rollingDaysRowCss,
  sectionCss,
  sectionHeaderCss,
  startIntervalFieldCss,
} from './styles'
import {
  captureScrollPositions,
  restoreScrollPositions,
} from '../../hooks/shell_scroll'

const getPeriodTypeOptions = () => [
  {
    value: 'rollingDays',
    text: I18n.t('publisher.event_type_editor_crud.availability.date_range_rolling'),
  },
  {
    value: 'fixed',
    text: I18n.t('publisher.event_type_editor_crud.availability.date_range_fixed'),
  },
  {
    value: 'unlimited',
    text: I18n.t('publisher.event_type_editor_crud.availability.date_range_indefinite'),
  },
]

const getNoticeTypeOptions = () => [
  { id: 'mins', name: I18n.t('publisher.event_types.one_off.units.minutes') },
  { id: 'hours', name: I18n.t('publisher.event_types.one_off.units.hours') },
  { id: 'days', name: I18n.t('publisher.event_types.one_off.units.days') },
]

const MinimumNoticeUnitSelect = forwardRef((props, ref) => (
  <SelectField
    ref={ref}
    {...props}
    className={classNames(minimumNoticeUnitSelectCss, props.className)}
  />
))

// Match legacy FixedOption / Figma date-range trigger (230px max, not full-bleed).
// TODO(cui-drift): DateRangePickerInput dialog footer alignment and range highlight
// colors are owned by @calendly/ui — track Figma parity in #calendly-ui.

const getDefaultFixedDateRange = (timezone) => {
  const start = moment.tz(timezone).startOf('day')
  const end = start.clone().add(6, 'months')

  return {
    startDate: start.format('YYYY-MM-DD'),
    endDate: end.format('YYYY-MM-DD'),
  }
}

const FixedDateRangeField = () => {
  const timezone = useSelector(timezoneSelector)
  const dateNotation = useSelector(dateNotationSelector)
  const {
    values: { startDate, endDate },
    errors: { startDate: error },
    setFieldValue,
    setFieldTouched,
  } = useFormContext()
  const errorId = useErrorId({ error })

  const onChange = ([start, end]) => {
    setFieldTouched('startDate', true)
    setFieldTouched('endDate', true)
    setFieldValue('startDate', start, true)
    setFieldValue('endDate', end)
  }

  const hasDateRange = Boolean(startDate && endDate)
  const displayValue = hasDateRange
    ? getPrettyDateRange({ startDate, endDate, dateNotation })
    : undefined

  return (
    <div className={dateRangeDetailCss}>
      <DateRangePickerInput
        aria-label={I18n.t('publisher.event_types.availability.booking_range_fieldset.fixed_label')}
        aria-describedby={errorId}
        className={fixedDateRangeInputCss}
        endDate={endDate}
        onChange={onChange}
        placeholder={I18n.t('publisher.event_type_editor_crud.availability.date_range_fixed_placeholder')}
        startDate={startDate}
        timezone={timezone}
        value={displayValue}
      />
      <FieldError id={errorId} text={hasDateRange ? error : undefined} />
    </div>
  )
}

const IndefiniteDateRangeDetail = () => (
  <BodyText className={fieldHelperCss} color="muted" size="medium">
    {I18n.t('publisher.event_type_editor_crud.availability.date_range_indefinite_description')}
  </BodyText>
)

const MinimumNoticeFields = ({ isLocked }) => {
  useValidateOnChange('minBookingTime')
  useErrorAfterInitialTouch('minBookingTime')
  useValidateOnChange('minBookingTimeType')

  return (
    <div className={compactFieldGroupCss} data-testid="minimum-notice-fields">
      <LabelText id="availability-minimum-notice-label" size="large">
        {I18n.t('publisher.event_type_editor_crud.availability.minimum_notice_label')}
      </LabelText>
      <div className={minimumNoticeRowCss} data-testid="minimum-notice-row">
        <NumberField
          aria-labelledby="availability-minimum-notice-label"
          className={minimumNoticeNumberFieldCss}
          disabled={isLocked}
          name="minBookingTime"
        />
        <MinimumNoticeUnitSelect
          aria-label={I18n.t('publisher.event_types.availability.notice_period_fieldset.custom_unit_label')}
          disabled={isLocked}
          name="minBookingTimeType"
          options={getNoticeTypeOptions()}
        />
      </div>
      <BodyText className={fieldHelperCss} color="muted" size="medium">
        {I18n.t('publisher.event_type_editor_crud.availability.minimum_notice_description')}
      </BodyText>
    </div>
  )
}

const StartIntervalField = ({ isLocked }) => {
  useValidateOnChange('customSpotStep')
  useErrorAfterInitialTouch('customSpotStep')
  useValidateOnChange('customSpotStepType')

  return (
    <div className={compactFieldGroupCss} data-testid="start-interval-fields">
      <LabelText id="availability-start-interval-label" size="large">
        {I18n.t('publisher.event_type_editor_crud.availability.start_interval_label')}
      </LabelText>
      <div
        aria-labelledby="availability-start-interval-label"
        className={startIntervalFieldCss}
      >
        <Increments isLocked={isLocked} v3Style />
      </div>
      <BodyText className={fieldHelperCss} color="muted" size="medium">
        {I18n.t('publisher.event_type_editor_crud.availability.start_interval_description')}
      </BodyText>
    </div>
  )
}

export const SchedulingWindowSection = ({ isLocked = false }) => {
  const {
    values: { periodType, startDate, endDate },
    setFieldValue,
    setFieldTouched,
    setFieldError,
  } = useFormContext()
  const timezone = useSelector(timezoneSelector)

  const handlePeriodTypeChange = useCallback((event) => {
    const { value } = event.target

    if (value === periodType) return

    // Shell-level focus guard covers pointer scroll; keep positions stable if
    // period fields briefly affect layout while values update.
    const scrollPositions = captureScrollPositions(event.target)
    event.target.focus?.({ preventScroll: true })

    setFieldValue('periodType', value)
    setFieldTouched('periodType', true)
    setFieldError('startDate', undefined)

    if (value === 'fixed' && (!startDate || !endDate)) {
      const defaults = getDefaultFixedDateRange(timezone)
      setFieldValue('startDate', defaults.startDate)
      setFieldValue('endDate', defaults.endDate)
    }

    requestAnimationFrame(() => {
      restoreScrollPositions(scrollPositions)
    })
  }, [
    endDate,
    periodType,
    setFieldError,
    setFieldTouched,
    setFieldValue,
    startDate,
    timezone,
  ])

  useValidateOnChange('startDate')
  useValidateOnChange('maxBookingTime')
  useErrorAfterInitialTouch('startDate')

  return (
    <section className={sectionCss} data-testid="scheduling-window-section">
      <div className={sectionHeaderCss}>
        <TitleText level={3} size="medium">
          {I18n.t('publisher.event_type_editor_crud.availability.scheduling_window_title')}
        </TitleText>
        <BodyText color="muted" size="medium">
          {I18n.t('publisher.event_type_editor_crud.availability.scheduling_window_description')}
        </BodyText>
      </div>

      <div className={fieldGroupCss}>
        <LabelText size="large">
          {I18n.t('publisher.event_type_editor_crud.availability.date_range_label')}
        </LabelText>
        <div className={dateRangeSwitchCss}>
          <ViewSwitcher
            disabled={isLocked}
            name="availability-date-range"
            onChange={handlePeriodTypeChange}
            options={getPeriodTypeOptions()}
            value={periodType}
          />
        </div>
      </div>

      {/* Keep all period details mounted; toggling display avoids remount scroll jumps. */}
      <div
        className={dateRangeDetailCss}
        hidden={periodType !== 'rollingDays'}
        aria-hidden={periodType !== 'rollingDays'}
      >
        <div className={rollingDaysRowCss}>
          <BodyText size="medium">
            {I18n.t('publisher.event_type_editor_crud.availability.rolling_days_prefix')}
          </BodyText>
          <NumberField
            aria-label={I18n.t('publisher.event_types.availability.range_of_dates_label')}
            disabled={isLocked || periodType !== 'rollingDays'}
            name="maxBookingTime"
          />
          <BodyText size="medium">
            {I18n.t('publisher.event_type_editor_crud.availability.rolling_days_suffix')}
          </BodyText>
        </div>
      </div>

      <div hidden={periodType !== 'fixed'} aria-hidden={periodType !== 'fixed'}>
        <FixedDateRangeField />
      </div>
      <div hidden={periodType !== 'unlimited'} aria-hidden={periodType !== 'unlimited'}>
        <IndefiniteDateRangeDetail />
      </div>
      <div className={compactFieldStackCss}>
        <MinimumNoticeFields isLocked={isLocked} />
        <StartIntervalField isLocked={isLocked} />
      </div>
    </section>
  )
}
